import React, { createContext, useContext, useReducer, useEffect } from "react";
import {
  User,
  Transaction,
  Asset,
  IncomeSource,
  Category,
  Debt,
  Partner,
} from "../types";
import {
  calculateCurrentBalance,
  calculateMonthlyIncome,
  calculateMonthlyExpenses,
  calculateRunwayProjection,
  calculateExpenseBreakdown,
} from "../utils/calculations";
import { isDemoMode, getDemoData } from "../utils/demoData";

// Load initial state from localStorage or use default
function loadInitialState(): DataState {
  // Check if we're in demo mode
  if (isDemoMode()) {
    // Try to load existing demo state first
    const storedDemo = localStorage.getItem("runway-demo-state");
    if (storedDemo) {
      try {
        const parsed = JSON.parse(storedDemo);
        return {
          users: Array.isArray(parsed.users) ? parsed.users : [],
          transactions: Array.isArray(parsed.transactions)
            ? parsed.transactions
            : [],
          assets: Array.isArray(parsed.assets) ? parsed.assets : [],
          incomeSources: Array.isArray(parsed.incomeSources)
            ? parsed.incomeSources
            : [],
          categories: Array.isArray(parsed.categories) ? parsed.categories : [],
          debts: Array.isArray(parsed.debts) ? parsed.debts : [],
          partners: Array.isArray(parsed.partners) ? parsed.partners : [],
          currentUserId: parsed.currentUserId || null,
        };
      } catch (error) {
        console.error("Error parsing stored demo data:", error);
      }
    }

    // If no stored demo state, use fresh demo data
    const demoData = getDemoData();
    return {
      users: demoData.users,
      transactions: demoData.transactions,
      assets: demoData.assets,
      incomeSources: demoData.incomeSources,
      categories: [],
      debts: demoData.debts,
      partners: demoData.partners,
      currentUserId: demoData.currentUserId,
    };
  }

  const stored = localStorage.getItem("runway-app-state");
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return {
        users: Array.isArray(parsed.users) ? parsed.users : [],
        transactions: Array.isArray(parsed.transactions)
          ? parsed.transactions
          : [],
        assets: Array.isArray(parsed.assets) ? parsed.assets : [],
        incomeSources: Array.isArray(parsed.incomeSources)
          ? parsed.incomeSources
          : [],
        categories: Array.isArray(parsed.categories) ? parsed.categories : [],
        debts: Array.isArray(parsed.debts) ? parsed.debts : [],
        partners: Array.isArray(parsed.partners) ? parsed.partners : [],
        currentUserId: parsed.currentUserId || null,
      };
    } catch (error) {
      console.error("Error parsing stored data:", error);
      // If parsing fails, return empty state
      return {
        users: [],
        transactions: [],
        assets: [],
        incomeSources: [],
        categories: [],
        debts: [],
        partners: [],
        currentUserId: null,
      };
    }
  }
  return {
    users: [],
    transactions: [],
    assets: [],
    incomeSources: [],
    categories: [],
    debts: [],
    partners: [],
    currentUserId: null,
  };
}

type DataAction =
  | { type: "ADD_USER"; payload: User }
  | { type: "UPDATE_USER"; payload: User }
  | { type: "SET_CURRENT_USER"; payload: string }
  | { type: "ADD_TRANSACTION"; payload: Transaction }
  | { type: "UPDATE_TRANSACTION"; payload: Transaction }
  | { type: "DELETE_TRANSACTION"; payload: string }
  | { type: "ADD_INCOME_SOURCE"; payload: IncomeSource }
  | { type: "UPDATE_INCOME_SOURCE"; payload: IncomeSource }
  | { type: "DELETE_INCOME_SOURCE"; payload: string }
  | { type: "ADD_ASSET"; payload: Asset }
  | { type: "UPDATE_ASSET"; payload: Asset }
  | { type: "DELETE_ASSET"; payload: string }
  | { type: "ADD_CATEGORY"; payload: Category }
  | { type: "UPDATE_CATEGORY"; payload: Category }
  | { type: "DELETE_CATEGORY"; payload: string }
  | { type: "ADD_DEBT"; payload: Debt }
  | { type: "UPDATE_DEBT"; payload: Debt }
  | { type: "DELETE_DEBT"; payload: string }
  | { type: "ADD_PARTNER"; payload: Partner }
  | { type: "UPDATE_PARTNER"; payload: Partner }
  | { type: "DELETE_PARTNER"; payload: string }
  | { type: "CLEAR_ALL_DATA" }
  | { type: "CLEAR_FINANCIAL_DATA" }
  | { type: "SET_DEMO_MODE"; payload: boolean };

interface DataState {
  users: User[];
  transactions: Transaction[];
  assets: Asset[];
  incomeSources: IncomeSource[];
  categories: Category[];
  debts: Debt[];
  partners: Partner[];
  currentUserId: string | null;
}

const dataReducer = (state: DataState, action: DataAction): DataState => {
  let newState: DataState;

  switch (action.type) {
    case "SET_DEMO_MODE":
      if (action.payload) {
        // Enter demo mode
        const demoData = getDemoData();
        return {
          users: demoData.users,
          transactions: demoData.transactions,
          assets: demoData.assets,
          incomeSources: demoData.incomeSources,
          categories: [],
          debts: demoData.debts,
          partners: demoData.partners,
          currentUserId: demoData.currentUserId,
        };
      } else {
        // Exit demo mode - restore user's existing data or show onboarding
        const stored = localStorage.getItem("runway-app-state");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            return {
              users: Array.isArray(parsed.users) ? parsed.users : [],
              transactions: Array.isArray(parsed.transactions)
                ? parsed.transactions
                : [],
              assets: Array.isArray(parsed.assets) ? parsed.assets : [],
              incomeSources: Array.isArray(parsed.incomeSources)
                ? parsed.incomeSources
                : [],
              categories: Array.isArray(parsed.categories)
                ? parsed.categories
                : [],
              debts: Array.isArray(parsed.debts) ? parsed.debts : [],
              partners: Array.isArray(parsed.partners) ? parsed.partners : [],
              currentUserId: parsed.currentUserId || null,
            };
          } catch (error) {
            console.error("Error parsing stored data:", error);
            // If parsing fails, return empty state
            return {
              users: [],
              transactions: [],
              assets: [],
              incomeSources: [],
              categories: [],
              debts: [],
              partners: [],
              currentUserId: null,
            };
          }
        } else {
          // No stored data - return empty state for onboarding
          return {
            users: [],
            transactions: [],
            assets: [],
            incomeSources: [],
            categories: [],
            debts: [],
            partners: [],
            currentUserId: null,
          };
        }
      }

    case "ADD_USER":
      newState = {
        ...state,
        users: [...state.users, action.payload],
      };
      break;

    case "UPDATE_USER":
      newState = {
        ...state,
        users: state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user
        ),
      };
      break;

    case "SET_CURRENT_USER":
      newState = {
        ...state,
        currentUserId: action.payload,
      };
      break;

    case "ADD_TRANSACTION":
      newState = {
        ...state,
        transactions: [...state.transactions, action.payload],
      };
      break;

    case "UPDATE_TRANSACTION":
      newState = {
        ...state,
        transactions: state.transactions.map((transaction) =>
          transaction.id === action.payload.id ? action.payload : transaction
        ),
      };
      break;

    case "DELETE_TRANSACTION":
      newState = {
        ...state,
        transactions: state.transactions.filter(
          (transaction) => transaction.id !== action.payload
        ),
      };
      break;

    case "ADD_INCOME_SOURCE":
      newState = {
        ...state,
        incomeSources: [...state.incomeSources, action.payload],
      };
      break;

    case "UPDATE_INCOME_SOURCE":
      newState = {
        ...state,
        incomeSources: state.incomeSources.map((incomeSource) =>
          incomeSource.id === action.payload.id ? action.payload : incomeSource
        ),
      };
      break;

    case "DELETE_INCOME_SOURCE":
      newState = {
        ...state,
        incomeSources: state.incomeSources.filter(
          (incomeSource) => incomeSource.id !== action.payload
        ),
      };
      break;

    case "ADD_ASSET":
      newState = {
        ...state,
        assets: [...state.assets, action.payload],
      };
      break;

    case "UPDATE_ASSET":
      newState = {
        ...state,
        assets: state.assets.map((asset) =>
          asset.id === action.payload.id ? action.payload : asset
        ),
      };
      break;

    case "DELETE_ASSET":
      newState = {
        ...state,
        assets: state.assets.filter((asset) => asset.id !== action.payload),
      };
      break;

    case "ADD_CATEGORY":
      newState = {
        ...state,
        categories: [...state.categories, action.payload],
      };
      break;

    case "UPDATE_CATEGORY":
      newState = {
        ...state,
        categories: state.categories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        ),
      };
      break;

    case "DELETE_CATEGORY":
      newState = {
        ...state,
        categories: state.categories.filter(
          (category) => category.id !== action.payload
        ),
      };
      break;

    case "ADD_DEBT":
      newState = {
        ...state,
        debts: [...state.debts, action.payload],
      };
      break;

    case "UPDATE_DEBT":
      newState = {
        ...state,
        debts: state.debts.map((debt) =>
          debt.id === action.payload.id ? action.payload : debt
        ),
      };
      break;

    case "DELETE_DEBT":
      newState = {
        ...state,
        debts: state.debts.filter((debt) => debt.id !== action.payload),
      };
      break;

    case "ADD_PARTNER":
      newState = {
        ...state,
        partners: [...state.partners, action.payload],
      };
      break;

    case "UPDATE_PARTNER":
      newState = {
        ...state,
        partners: state.partners.map((partner) =>
          partner.id === action.payload.id ? action.payload : partner
        ),
      };
      break;

    case "DELETE_PARTNER":
      newState = {
        ...state,
        partners: state.partners.filter(
          (partner) => partner.id !== action.payload
        ),
      };
      break;

    case "CLEAR_ALL_DATA":
      newState = {
        users: [],
        transactions: [],
        assets: [],
        incomeSources: [],
        categories: [],
        debts: [],
        partners: [],
        currentUserId: null,
      };
      break;

    case "CLEAR_FINANCIAL_DATA":
      newState = {
        ...state,
        transactions: [],
        assets: [],
        incomeSources: [],
        categories: [],
        debts: [],
        partners: [],
      };
      break;

    default:
      return state;
  }

  return newState;
};

const DataContext = createContext<{
  state: DataState;
  dispatch: React.Dispatch<DataAction>;
  getCurrentUser: () => User | null;
  getDashboardData: () => any;
} | null>(null);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(dataReducer, loadInitialState());

  // Save to localStorage when state changes
  useEffect(() => {
    try {
      if (isDemoMode()) {
        // In demo mode, save to a separate demo-specific key
        localStorage.setItem("runway-demo-state", JSON.stringify(state));
      } else {
        // In normal mode, save to the regular key
        localStorage.setItem("runway-app-state", JSON.stringify(state));
      }
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, [state]);

  const getCurrentUser = () => {
    if (!state.currentUserId) return null;
    return state.users.find((user) => user.id === state.currentUserId) || null;
  };

  const getDashboardData = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return null;

    // Get user-specific data for display purposes
    const userTransactions = state.transactions.filter(
      (t) => t.userId === currentUser.id
    );
    const userIncomeSources = state.incomeSources.filter(
      (i) => i.userId === currentUser.id
    );
    const userAssets = state.assets.filter((a) => a.userId === currentUser.id);
    const userDebts = state.debts.filter((d) => d.userId === currentUser.id);

    // Get active partner data
    const activePartners = state.partners.filter(
      (p) => p.userId === currentUser.id && p.isActive
    );

    // Combine user data with active partner data for calculations
    let allTransactions = [...userTransactions];
    let allIncomeSources = [...userIncomeSources];
    let allAssets = [...userAssets];
    let allDebts = [...userDebts];

    // Add partner summary data to the calculations
    activePartners.forEach((partner) => {
      // Add partner's liquid assets as an asset
      if (partner.liquidAssets > 0) {
        allAssets.push({
          id: `partner-liquid-${partner.id}`,
          userId: partner.id,
          name: `${partner.name}'s Liquid Assets`,
          type: "savings",
          balance: partner.liquidAssets,
          isLiquid: true,
          createdAt: partner.createdAt,
        });
      }

      // Note: Partner's monthly income and expenses are handled separately
      // in the calculation functions that use the partner data
    });

    // Calculate financial metrics using combined data (user + active partners)
    const currentBalance = calculateCurrentBalance(
      allTransactions,
      currentUser.id,
      allAssets,
      allDebts
    );

    // Calculate monthly income including partner data
    let monthlyIncome = calculateMonthlyIncome(
      allTransactions,
      currentUser.id,
      allIncomeSources
    );
    // Add partner monthly income
    activePartners.forEach((partner) => {
      monthlyIncome += partner.monthlyIncome;
    });

    // Calculate monthly expenses including partner data
    let monthlyExpenses = calculateMonthlyExpenses(
      allTransactions,
      currentUser.id
    );
    // Add partner monthly expenses
    activePartners.forEach((partner) => {
      monthlyExpenses += partner.monthlyExpenses;
    });
    const runwayProjection = calculateRunwayProjection(
      currentBalance,
      monthlyIncome,
      monthlyExpenses
    );
    const expenseBreakdown = calculateExpenseBreakdown(
      allTransactions,
      currentUser.id
    );

    // Get recent transactions (last 10) - show user's transactions for display
    const recentTransactions = userTransactions
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10);

    return {
      currentBalance,
      monthlyIncome,
      monthlyExpenses,
      runwayProjection,
      expenseBreakdown,
      recentTransactions,
      assets: allAssets, // Show combined assets (user + partner) for display
      debts: userDebts, // Show user's debts for display
      incomeSources: userIncomeSources, // Show user's income sources for display
    };
  };

  return (
    <DataContext.Provider
      value={{
        state,
        dispatch,
        getCurrentUser,
        getDashboardData,
      }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
